import { c as _c } from "react/compiler-runtime";
import { getNextMintStart } from "@/components/meme-calendar/meme-calendar.helpers";
import ClockIcon from "@/components/utils/icons/ClockIcon";
import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import type { ReactNode } from "react";
type StatusType = "error" | "sold_out" | "finalized";
interface StatusConfig {
  icon: ReactNode;
  iconBg: string;
  iconRing: string;
  topLeftText: string;
  blackDivText: string;
}
const STATUS_CONFIGS: Record<StatusType, StatusConfig> = {
  error: {
    icon: <ExclamationTriangleIcon className="tw-size-5 tw-text-[#C9A879]" />,
    iconBg: "tw-bg-[#C9A879]/10",
    iconRing: "tw-ring-[#C9A879]/20",
    topLeftText: "Error",
    blackDivText: "Error fetching mint information. Please try again later."
  },
  sold_out: {
    icon: <CheckCircleIcon className="tw-size-[30px] tw-flex-shrink-0 tw-text-emerald-300/80" />,
    iconBg: "tw-bg-iron-900/60",
    iconRing: "tw-ring-white/10",
    topLeftText: "Mint Complete",
    blackDivText: "All editions have been minted!"
  },
  finalized: {
    icon: <ClockIcon className="tw-size-7 tw-text-iron-300" />,
    iconBg: "tw-bg-white/5",
    iconRing: "tw-ring-white/10",
    topLeftText: "Mint Complete",
    blackDivText: "Minting for this drop has ended."
  }
};
interface NowMintingCountdownStatusProps {
  readonly type: StatusType;
  readonly hideNextDrop?: boolean;
}
export default function NowMintingCountdownStatus(t0) {
  const $ = _c(18);
  const {
    type,
    hideNextDrop
  } = t0;
  const config = STATUS_CONFIGS[type];
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    const nextMintDate = getNextMintStart();
    t1 = nextMintDate.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric"
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const formattedDate = t1;
  let t2;
  if ($[1] !== config.topLeftText) {
    t2 = <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between"><span className="tw-py-0.5 tw-text-xs tw-font-semibold tw-text-iron-300">{config.topLeftText}</span></div>;
    $[1] = config.topLeftText;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = `tw-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-ring-1 ${config.iconBg} ${config.iconRing}`;
  let t4;
  if ($[3] !== config.icon || $[4] !== t3) {
    t4 = <div className={t3}>{config.icon}</div>;
    $[3] = config.icon;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== config.blackDivText) {
    t5 = <span className="tw-text-sm tw-font-medium tw-text-iron-100">{config.blackDivText}</span>;
    $[6] = config.blackDivText;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = <div className="tw-flex tw-h-14 tw-items-center tw-justify-center tw-gap-3 tw-rounded-md tw-border tw-border-white/10 tw-bg-iron-950 tw-shadow-[inset_0_1px_2px_rgba(0,0,0,0.5)]">{t4}{t5}</div>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== hideNextDrop || $[12] !== type) {
    t7 = type !== "error" && !hideNextDrop && <div className="tw-mt-3 tw-flex tw-h-10 tw-w-full tw-items-center tw-justify-center tw-gap-x-1.5 tw-whitespace-nowrap tw-rounded-lg tw-border-0 tw-bg-iron-200 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-iron-950">Next drop {formattedDate}</div>;
    $[11] = hideNextDrop;
    $[12] = type;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t2 || $[15] !== t6 || $[16] !== t7) {
    t8 = <div className="tw-relative tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-white/5 tw-bg-iron-900/60 tw-p-3 tw-text-left md:tw-p-4">{t2}{t6}{t7}</div>;
    $[14] = t2;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}